import { fetchAPI, METHODS, parseBody, assertErrors } from './common';

/**
 * Get a Firebase custom token for authenticating with Firestore.
 *
 * @returns {Promise<string>} The Firebase custom token.
 */
export async function getFirebaseToken() {
  const res = await fetchAPI('/chat/firebase-token', METHODS.get);
  await assertErrors(res);
  const { firebaseToken } = await parseBody(res);
  return firebaseToken;
}

/**
 * Find an existing team-wide conversation (does not create one).
 *
 * @returns {Promise<string|null>} The conversation ID, or null if none exists.
 */
export async function getTeamConversation() {
  const res = await fetchAPI('/chat/conversation/team', METHODS.get);
  await assertErrors(res);
  const { conversationId } = await parseBody(res);
  return conversationId;
}

export async function createTeamConversation() {
  const res = await fetchAPI('/chat/conversation/team', METHODS.post);
  await assertErrors(res);
  const { conversationId } = await parseBody(res);
  return conversationId;
}

export async function getAdminTeamConversation() {
  const res = await fetchAPI('/chat/conversation/admin-team', METHODS.get);
  await assertErrors(res);
  const { conversationId } = await parseBody(res);
  return conversationId;
}

export async function createAdminTeamConversation() {
  const res = await fetchAPI('/chat/conversation/admin-team', METHODS.post);
  await assertErrors(res);
  const { conversationId } = await parseBody(res);
  return conversationId;
}

export async function getAdminAllChats(params = {}) {
  const qp = new URLSearchParams();
  if (params.search) qp.set('search', params.search);
  if (params.participant) qp.set('participant', params.participant);
  if (params.type) qp.set('type', params.type);
  if (params.status) qp.set('status', params.status);
  if (params.sortBy) qp.set('sortBy', params.sortBy);
  if (params.limit) qp.set('limit', params.limit);
  if (params.offset) qp.set('offset', params.offset);
  const qs = qp.toString();
  const res = await fetchAPI(
    `/chat/admin/all${qs ? `?${qs}` : ''}`,
    METHODS.get
  );
  await assertErrors(res);
  return await parseBody(res);
}

export async function getAdminChatStats() {
  const res = await fetchAPI('/chat/admin/stats', METHODS.get);
  await assertErrors(res);
  return await parseBody(res);
}

export async function adminToggleArchive(conversationSid, archived) {
  const res = await fetchAPI(
    `/chat/admin/conversation/${conversationSid}/archive`,
    METHODS.post,
    { archived }
  );
  await assertErrors(res);
}

export async function getChatParticipants(conversationSid) {
  const res = await fetchAPI(
    `/chat/admin/conversation/${conversationSid}/participants`,
    METHODS.get
  );
  await assertErrors(res);
  return await parseBody(res);
}

export async function getChatMessages(conversationSid) {
  const res = await fetchAPI(
    `/chat/admin/conversation/${conversationSid}/messages`,
    METHODS.get
  );
  await assertErrors(res);
  return await parseBody(res);
}

/**
 * Find an existing conversation for a donation (does not create one).
 *
 * @param {string} donationId
 * @returns {Promise<string|null>} The conversation ID, or null if none exists.
 */
export async function getDonationConversation(donationId, rescueId) {
  const res = await fetchAPI(
    `/chat/conversation/donation/${donationId}${
      rescueId ? `?rescueId=${rescueId}` : ''
    }`,
    METHODS.get
  );
  await assertErrors(res);
  const { conversationId } = await parseBody(res);
  return conversationId;
}

/**
 * Create (or find existing) conversation for a donation.
 * Called on first message send.
 *
 * @param {string} donationId
 * @param {string} [rescueId]
 * @returns {Promise<string>} The conversation ID.
 */
export async function createDonationConversation(donationId, rescueId) {
  const res = await fetchAPI(
    `/chat/conversation/donation/${donationId}${
      rescueId ? `?rescueId=${rescueId}` : ''
    }`,
    METHODS.post
  );
  await assertErrors(res);
  const { conversationId } = await parseBody(res);
  return conversationId;
}

/**
 * Find an existing conversation for a marketplace listing (does not create
 * one).
 *
 * @param {string} listingId
 * @returns {Promise<string|null>} The conversation ID, or null if none exists.
 */
export async function getListingConversation(listingId) {
  const res = await fetchAPI(
    `/chat/conversation/marketplace-listing/${listingId}`,
    METHODS.get
  );
  await assertErrors(res);
  const { conversationId } = await parseBody(res);
  return conversationId;
}

/**
 * Create (or find existing) conversation for a marketplace listing.
 *
 * @param {string} listingId
 * @returns {Promise<string>} The conversation ID.
 */
export async function createListingConversation(listingId) {
  const res = await fetchAPI(
    `/chat/conversation/marketplace-listing/${listingId}`,
    METHODS.post
  );
  await assertErrors(res);
  const { conversationId } = await parseBody(res);
  return conversationId;
}
